import React, { useState, useEffect, useCallback } from 'react';
import './Styles.css';
import { PresentationMode } from '../../../../Enums/PresentationMode';
import { ServiceClient } from '../../../../Services/ServiceClient';
import Modal from '../../../../Elements/ids/Modal';
import EmployerAddressButtons from './Buttons';
import AddressFormatSwitcher from '../../../../Elements/AddressFormatSwitcher';
import LoaderComponent from '../../../../Elements/ids/Loader';
import { useGlobalContext } from '../../../../context/GlobalContext';
import { AddressFormats } from '../../../../Enums/AddressFormats';
import Form from '../../../../Elements/Form';
import { useDebouncedEffect } from '../../../../Helpers/CustomHooks';
import { areFieldsValid, hasAnyFieldChanged, mapAddressFieldsToApiFormat } from '../../../../Helpers/AddressHelper';
import { createServerErrorMessage } from '../../../../Helpers/FormInputHelper';
import ErrorMessage from '../../../../Elements/ErrorMessage';
import { mapGetEmployerResponse } from '../../Functions';
import FormatSelector from './FormatSelector';
import { formatMessage } from '../../../../Localization/formatMessage';
import Messages from '../../../../Localization/Messages';

export const employerAddressFormId = 'employerAddressFormId';

const EmployerAddress = ({ employer, setEmployer, closeView }) => {
  const { legalUnit } = useGlobalContext();

  const [canSubmit, setCanSubmit] = useState(false);
  const [modalMode, setModalMode] = useState(PresentationMode.Loading);
  const [countryList, setCountryList] = useState([]);
  const [addressState, setAddressState] = useState({});

  const getCommonFields = (propertyName, isMandatory = false) => {
    return {
      name: propertyName,
      key: propertyName,
      error: addressState?.errors[propertyName],
      isMandatory: isMandatory,
      value: addressState?.address[propertyName] ?? '',
      onChange: event => {
        setAddressState({ ...addressState, address: { ...addressState.address, [propertyName]: event.target.value } });
      },
    };
  };

  const validateAddressInfo = useCallback(() => {
    if (!addressState.address && modalMode !== PresentationMode.Add && modalMode !== PresentationMode.Edit) {
      return;
    }

    const validationResult = areFieldsValid(addressState.address);
    setCanSubmit(
      modalMode === PresentationMode.Edit
        ? validationResult && hasAnyFieldChanged(addressState.address, addressState.uneditedAddress)
        : validationResult
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addressState.address, modalMode]);

  useDebouncedEffect(validateAddressInfo, 500);

  useEffect(() => {
    ServiceClient.getCountries(countries => {
      setCountryList(countries);
      if (employer.address) {
        setModalMode(PresentationMode.Edit);
        setAddressState({ address: employer.address, uneditedAddress: employer.address, errors: {} });
      } else {
        setModalMode(PresentationMode.Add);
        setAddressState({
          address: {
            format: AddressFormats.Street,
            country: countries.find(x => x.code === legalUnit),
          },
          errors: {},
        });
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSubmit = event => {
    event.preventDefault();

    ServiceClient.createNewAddress(
      mapAddressFieldsToApiFormat(addressState.address),
      addressId => {
        ServiceClient.updateEmployerInfo(
          { id: employer.id, addressId: addressId },
          16,
          data => {
            setEmployer(mapGetEmployerResponse(data));
            setAddressState({ ...addressState, uneditedAddress: data.address, address: data.address });
            setModalMode(PresentationMode.Edit);
          },
          error => {
            setModalMode(PresentationMode.Error);
            setAddressState({
              ...addressState,
              errors: { ...addressState.errors, server: createServerErrorMessage(error).message },
            });
          }
        );
      },
      error => {
        setModalMode(PresentationMode.Error);
        setAddressState({
          ...addressState,
          errors: { ...addressState.errors, server: createServerErrorMessage(error).message },
        });
      }
    );
    return;
  };

  return (
    <Modal
      titleText={formatMessage(Messages.employerPage_employerAddress_title)}
      closeView={closeView}
      buttons={
        <EmployerAddressButtons
          closeView={closeView}
          canSubmit={canSubmit}
          modalMode={modalMode}
          closeErrorView={() => {
            setModalMode(employer.address ? PresentationMode.Edit : PresentationMode.Add);
            setAddressState({ ...addressState, errors: { ...addressState.errors, server: null } });
          }}
        />
      }>
      <Form formId={employerAddressFormId} onSubmit={onSubmit} cssClasses="employerPage-employerAddress-container ">
        {modalMode === PresentationMode.Loading ? (
          <LoaderComponent />
        ) : modalMode === PresentationMode.Edit || modalMode === PresentationMode.Add ? (
          <>
            <FormatSelector addressState={addressState} setAddressState={setAddressState} />
            <AddressFormatSwitcher
              addressState={addressState}
              modalMode={modalMode}
              getCommonFields={getCommonFields}
              countryList={countryList}
              setAddressState={setAddressState}
            />
          </>
        ) : modalMode === PresentationMode.Error ? (
          <ErrorMessage message={addressState.errors?.server} />
        ) : null}
      </Form>
    </Modal>
  );
};

export default EmployerAddress;
